<template>
  <div class="tc-events-panel" style="border-right:none;border-left:1px solid var(--stroke-1)">
    <div class="tc-events-filter-bar" style="justify-content:space-between;align-items:center">
      <span style="font-size:var(--t-14);font-weight:var(--w-semibold);color:var(--fg-primary)">Events · Apr 16–30</span>
      <button
        type="button"
        class="tc-btn-icon"
        aria-label="Close inspector"
        @click="emit('close')"
      >
        <svg viewBox="0 0 18 18" fill="none" aria-hidden="true">
          <path d="M13.5 4.5L4.5 13.5M4.5 4.5l9 9" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
        </svg>
      </button>
    </div>

    <div class="tc-events-filter-bar">
      <button
        v-for="pill in FILTER_PILLS"
        :key="pill.id"
        type="button"
        class="tc-filter-pill"
        :class="{ active: activeFilter === pill.id }"
        @click="activeFilter = pill.id"
      >
        {{ pill.label }}
        <span class="tc-filter-count">{{ countFor(pill.id) }}</span>
      </button>
    </div>

    <div class="tc-events-scroll tc-scroll">
      <div v-if="filteredGroups.length === 0" class="tc-events-empty">
        No events in this view.
      </div>
      <div v-for="group in filteredGroups" :key="group.day" class="tc-events-day-group">
        <div class="tc-events-day-label">
          <span>{{ group.label }}</span>
          <span class="tc-events-day-count">{{ group.events.length }} events</span>
        </div>
        <ul class="tc-events-list">
          <li
            v-for="ev in group.events"
            :key="ev.id"
            class="tc-event-card"
            :class="[
              ev.id === selectedEventId ? 'selected' : '',
              statusCardClass(ev.status),
            ]"
          >
            <div class="conf-bar"></div>
            <div class="tc-event-card-meta">
              <span
                v-if="ev.source === 'Freshdesk'"
                class="tc-event-source-badge tc-event-source-freshdesk"
                title="Freshdesk"
                aria-label="Freshdesk"
              >F</span>
              <span
                v-else
                class="tc-event-source-badge tc-event-source-outlook"
                title="Outlook"
                aria-label="Outlook"
              >O</span>
              <span class="tc-event-card-time">{{ ev.time }}</span>
              <span
                v-if="ev.status === 'attributed' && ev.wbs"
                class="ms-auto tc-event-card-time"
                style="color:var(--brand-primary);font-weight:var(--w-semibold)"
              >→ {{ ev.wbs }}</span>
              <span
                v-else-if="ev.status === 'needs-input'"
                class="ms-auto tc-event-card-time"
                style="color:var(--conf-med);font-weight:var(--w-semibold)"
              >Needs input</span>
              <span
                v-else-if="ev.status === 'filtered'"
                class="ms-auto tc-event-card-time"
                style="color:var(--fg-tertiary)"
              >Filtered</span>
            </div>
            <div class="tc-event-card-title">{{ ev.title }}</div>
            <div
              v-if="ev.status === 'filtered' && ev.reason"
              style="margin-top:var(--s-4);font-size:var(--t-12);color:var(--fg-tertiary);font-style:italic"
            >{{ ev.reason }}</div>
            <div v-if="ev.status === 'filtered'" class="mt-2">
              <button type="button" class="btn btn-outline-secondary btn-sm">Attribute…</button>
            </div>
          </li>
        </ul>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue';

type EventStatus = 'attributed' | 'needs-input' | 'filtered';

interface CalEvent {
  id: string;
  time: string;
  title: string;
  source?: string;
  wbs?: string;
  status: EventStatus;
  reason?: string;
}

interface EventGroup {
  day: string;
  label: string;
  events: CalEvent[];
}

defineProps<{
  selectedEventId?: string;
}>();

const emit = defineEmits<{
  close: [];
}>();

type FilterId = 'all' | 'attributed' | 'needs-input' | 'filtered';

const activeFilter = ref<FilterId>('all');

const FILTER_PILLS: { id: FilterId; label: string }[] = [
  { id: 'all', label: 'All' },
  { id: 'attributed', label: 'Attributed' },
  { id: 'needs-input', label: 'Needs input' },
  { id: 'filtered', label: 'Filtered' },
];

const ALL_GROUPS: EventGroup[] = [
  {
    day: 'mon-16',
    label: 'Mon 16',
    events: [
      { id: 'ev-1', time: '09:00', title: 'Acme weekly sync', wbs: 'ACME', status: 'attributed' },
      { id: 'ev-2', time: '09:30', title: 'Northwind discovery call', wbs: 'NW', status: 'attributed' },
      { id: 'ev-3', time: '14:00', title: 'Internal team standup', wbs: 'MTG', status: 'attributed' },
    ],
  },
  {
    day: 'tue-17',
    label: 'Tue 17',
    events: [
      { id: 'ev-4', time: '09:00', title: 'Acme weekly sync', wbs: 'ACME', status: 'attributed' },
      { id: 'ev-5', time: '09:35', title: 'Acme SSO ticket', source: 'Freshdesk', wbs: 'ACME', status: 'attributed' },
      { id: 'ev-6', time: '10:00', title: 'External call w/ Globex', status: 'needs-input' },
      { id: 'ev-7', time: '11:00', title: 'Northwind design review', wbs: 'NW', status: 'attributed' },
      { id: 'ev-x1', time: '12:30', title: 'Lunch with Sarah', status: 'filtered', reason: 'Personal — agent skipped' },
    ],
  },
  {
    day: 'wed-18',
    label: 'Wed 18',
    events: [
      { id: 'ev-8', time: '10:00', title: 'Northwind architecture discussion', wbs: 'NW', status: 'attributed' },
      { id: 'ev-9', time: '14:30', title: 'Internal team standup', wbs: 'MTG', status: 'attributed' },
      { id: 'ev-x2', time: '17:00', title: 'Gym', status: 'filtered', reason: 'Recurring personal event' },
    ],
  },
  {
    day: 'thu-19',
    label: 'Thu 19',
    events: [
      { id: 'ev-10', time: '09:00', title: 'Acme support review', wbs: 'ACME', status: 'attributed' },
      { id: 'ev-11', time: '09:35', title: 'Cleaning up old tickets', source: 'Freshdesk', status: 'needs-input' },
      { id: 'ev-12', time: '14:00', title: 'Internal team standup', wbs: 'MTG', status: 'attributed' },
    ],
  },
  {
    day: 'fri-20',
    label: 'Fri 20',
    events: [
      { id: 'ev-13', time: '09:00', title: 'Admin / timesheets', wbs: 'OH-PROD', status: 'attributed' },
      { id: 'ev-x3', time: '12:00', title: 'All-day OOO', status: 'filtered', reason: 'All-day event' },
    ],
  },
];

const counts = computed(() => {
  const c: Record<FilterId, number> = { all: 0, attributed: 0, 'needs-input': 0, filtered: 0 };
  for (const g of ALL_GROUPS) {
    for (const e of g.events) {
      c.all += 1;
      c[e.status] += 1;
    }
  }
  return c;
});

function countFor(id: FilterId): number {
  return counts.value[id];
}

const filteredGroups = computed((): EventGroup[] => {
  const f = activeFilter.value;
  if (f === 'all') return ALL_GROUPS;
  return ALL_GROUPS
    .map(g => ({ ...g, events: g.events.filter(e => e.status === f) }))
    .filter(g => g.events.length > 0);
});

function statusCardClass(status: EventStatus): string {
  if (status === 'attributed') return 'conf-confirmed';
  if (status === 'needs-input') return 'conf-med';
  return 'conf-dismissed';
}
</script>
